import { useAnalytics } from '@/services/analytics';
import { setUserSettings } from '@/services/api';
import { useAuth } from '@/services/auth';
import { IUserSettingsData } from '@d-id/types';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { fillDBObjectWithDefaultValuesIfEmpty } from './constants';
import { USER_SETTINGS_KEY, useUserSettingsFromDB } from './userSettings';

type ExtendedUserSettings = {
    updateUserSettings: (properties: IUserSettingsData, refetchOnEnd?: boolean) => Promise<void>;
    userSettings: IUserSettingsData;
    loading: boolean;
    shouldOnBoardingDialogBeOpen: boolean;
    setShouldOnBoardingDialogBeOpen: any;
    userSettingsUpdating: boolean;
};

const Store = React.createContext<ExtendedUserSettings>({} as ExtendedUserSettings);
Store.displayName = 'user-settings';
export const useUserSettings = () => React.useContext(Store);

export const UserSettingsProvider = ({ children }: any) => {
    const [instance] = useState<IUserSettingsData | null | undefined>(null);
    const mixpanel = useAnalytics();
    const { user, isAnonymous, isAuthenticated } = useAuth();
    const { isLoading, data } = useUserSettingsFromDB({ enabled: !!user && !isAnonymous });
    const [shouldOnBoardingDialogBeOpen, setShouldOnBoardingDialogBeOpen] = useState(true);
    const queryClient = useQueryClient();
    const [userSettingsUpdating, setUserSettingsUpdating] = useState(false);
    
    useEffect(() => {
        if (mixpanel && data?.features?.editor?.version) {
            mixpanel.enrich({ editorVersion: data?.features?.editor?.version });
        }
    }, [data?.features?.editor?.version, mixpanel]);

    useEffect(() => {
        const isOnboarding = !!(user?.email_verified && !data?.features?.onBoarding?.finished && isAuthenticated && !isAnonymous);
        if (!isLoading) {
            setShouldOnBoardingDialogBeOpen(isOnboarding);
        }
    }, [user, isAnonymous, isAuthenticated, instance?.features?.onBoarding?.finished]);

    async function updateUserSettings(settings: IUserSettingsData, refetchOnEnd = true) {
        setUserSettingsUpdating(true);
        try {
            const updatedData = await setUserSettings(settings);
            if (refetchOnEnd) {
                queryClient.setQueriesData(USER_SETTINGS_KEY, updatedData);
                queryClient.refetchQueries(USER_SETTINGS_KEY);
            }
        } finally {
            setUserSettingsUpdating(false);
        }
    }

    function getUserSettings() {
        if (!isLoading) {
            return fillDBObjectWithDefaultValuesIfEmpty(data);
        }
    }

    const userSettings: ExtendedUserSettings = {
        updateUserSettings,
        userSettings: getUserSettings() || {},
        loading: isLoading,
        shouldOnBoardingDialogBeOpen,
        setShouldOnBoardingDialogBeOpen,
        userSettingsUpdating,
    };

    return <Store.Provider value={userSettings}>{children}</Store.Provider>;
};

export default UserSettingsProvider;
